import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';

import { EmailVerificationErrorState } from 'utils/emailVerificationErrors';

/**
 * Props for the VerificationStatusAlert component.
 */
interface VerificationStatusAlertProps {
  /** True when verification or resend operation is in progress */
  loading: boolean;
  /** True when email verification was successful */
  success: boolean;
  /** Error state with message and optional action, or null if no error */
  error: EmailVerificationErrorState | null;
  /** Callback to resend verification email */
  onResend: () => void;
  /** Callback to retry verification */
  onRetry: () => void;
}

/**
 * Displays status alerts for email verification with appropriate actions.
 * Renders both success and error alerts, using Collapse to animate transitions.
 */
export function VerificationStatusAlert({
  loading,
  success,
  error,
  onResend,
  onRetry,
}: VerificationStatusAlertProps) {
  const actionButton =
    error?.action && error?.actionTitle ? (
      <Button
        color="inherit"
        size="small"
        disabled={loading}
        onClick={error.action === 'resend' ? onResend : onRetry}
      >
        {error.actionTitle}
      </Button>
    ) : undefined;

  return (
    <Stack>
      {/* Success alert */}
      <Collapse in={success && !loading}>
        <Alert variant="outlined" severity="success">
          Your email has been verified successfully!
        </Alert>
      </Collapse>

      {/* Error/Info alert */}
      <Collapse in={!!error && !success && !loading}>
        <Alert variant="outlined" severity={error?.severity ?? 'error'} action={actionButton}>
          {error?.message}
        </Alert>
      </Collapse>
    </Stack>
  );
}
